import _ from 'underscore';
import { odata } from '@bingads-webui/http-util';
import type { Flight } from './index.d';
import { isLocalHostname } from './utils';

export interface AccountFlightParams {
  aid: number;
  cid: number;
  campaignApiBase?: string;
  oDataToken?: string;
  activity: any;
}

export interface CustomerFlightParams {
  cid: number;
  campaignApiBase?: string;
  oDataToken?: string;
  activity: any;
}

export interface CampaignFlightParams {
  campaignId: number;
  cid: number;
  aid: number;
  campaignApiBase?: string;
  oDataToken?: string;
  activity: any;
}

const buildBaseUrl = (campaignApiBase?: string): string => {
  const base = campaignApiBase ?? '/ODataApi/';
  const odataPath = isLocalHostname(document.location.hostname) ? 'Advertiser/V2' : 'Campaign/V2';
  return `${base}${odataPath}`;
};

const fetchFlightAllocations = (
  url: string,
  oDataToken: string | undefined,
  activity: any,
  buildTrace: (flights: Flight[]) => Record<string, unknown>
): Promise<Flight[]> =>
  odata.get(url, {
    ...(oDataToken ? { oDataToken } : {}),
  }).then((response: unknown) => {
    const flights = response as Flight[];
    activity.trace(JSON.stringify(buildTrace(flights)));
    return flights;
  });

export const getAccountFlightAllocations = _.memoize(
  ({
    aid, cid, campaignApiBase, oDataToken, activity,
  }: AccountFlightParams): Promise<Flight[]> =>
    fetchFlightAllocations(
      `${buildBaseUrl(campaignApiBase)}/FlightAllocations/CustomerId/${cid}/AccountId/${aid}`,
      oDataToken,
      activity,
      flights => ({ aid, AccountFlights: flights })
    ),
  ({ aid, cid, campaignApiBase }: AccountFlightParams): string => `${campaignApiBase ?? ''}-${cid}-${aid}`
);

export const getCustomerFlightAllocations = _.memoize(
  ({
    cid, campaignApiBase, oDataToken, activity,
  }: CustomerFlightParams): Promise<Flight[]> =>
    fetchFlightAllocations(
      `${buildBaseUrl(campaignApiBase)}/FlightAllocations/CustomerId/${cid}`,
      oDataToken,
      activity,
      flights => ({ cid, CustomerFlights: flights })
    ),
  ({ cid, campaignApiBase }: CustomerFlightParams): string => `${campaignApiBase ?? ''}-${cid}`
);

export const getCampaignFlightAllocations = _.memoize(
  ({
    campaignId, cid, aid, campaignApiBase, oDataToken, activity,
  }: CampaignFlightParams): Promise<Flight[]> =>
    fetchFlightAllocations(
      `${buildBaseUrl(campaignApiBase)}/FlightAllocations/CustomerId/${cid}/AccountId/${aid}/CampaignId/${campaignId}`,
      oDataToken,
      activity,
      flights => ({ campaignId, CampaignFlights: flights })
    ),
  ({ campaignId, cid, aid }: CampaignFlightParams): string => `${cid}-${aid}-${campaignId}`
);
